// Seller-side reservation queue client (TNK-51 Phase 4). Authed — uses apiFetch
// so the bearer token rides along and a 401 triggers the app's logout handler.

import { apiFetch } from "@/lib/apiClient";
import type { ReservationItemView } from "./storefront";

/** TNK-127 phase 2 — formal priced offer attached to a reservation. */
export interface ReservationOffer {
  id: string;
  reservationId: string;
  /** sent | accepted | countered | declined | expired | cancelled.
   *  `expired` is read-side: the API treats a `sent` offer past
   *  `expiresAt` as expired even though the DB row stays `sent`
   *  until phase 5's background job. */
  status: string;
  /** "admin" today. Phase 4 introduces buyer counters. */
  createdByType: string;
  subtotal: number;
  discount: number;
  shipping: number;
  total: number;
  message: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  declinedAt: string | null;
  createdAt: string;
}

export interface SellerReservation {
  id: string;
  buyerEmail: string;
  buyerName: string | null;
  buyerNote: string | null;
  status: string;
  pickupPreference: string;
  shippingAddress: Record<string, string> | null;
  sellerNote: string | null;
  /** TNK-127 phase 1 — internal seller review notes, never shown to the buyer. */
  adminNotes: string | null;
  requestedAt: string;
  expiresAt: string;
  /** Snapshot items captured at submit time, with live availability + price
   *  layered on. See `ReservationItemView` in storefront.ts. */
  cards: ReservationItemView[];
  /** TNK-127 phase 2 — latest offer (if any). Null until the seller
   *  sends one. The buyer page renders Accept/Decline from this. */
  currentOffer: ReservationOffer | null;
  /** TNK-127 phase 3 — manual payment link the operator pasted for an
   *  AwaitingPayment reservation; surfaced to the buyer as "Pay now". */
  paymentLinkUrl: string | null;
}

/** TNK-127 phase 1 — `notes-only` saves admin notes without changing
 *  lifecycle status. Useful for "writing down what you're checking"
 *  during review before deciding accept/decline. */
export type ReservationAction =
  | "accept"
  | "decline"
  | "complete"
  | "notes-only"
  // TNK-127 phase 3 — close an AwaitingPayment reservation to Paid (the client
  // turns the held cards into a bundle product just before calling this).
  | "mark-paid";

/** Returns [] when the storefront feature is disabled on the server (404). */
export async function fetchSellerReservations(): Promise<SellerReservation[]> {
  const res = await apiFetch("/api/reservations");
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(`Reservations unavailable (${res.status})`);
  const data = (await res.json()) as { reservations: SellerReservation[] };
  return data.reservations;
}

export async function patchReservation(
  id: string,
  action: ReservationAction,
  payload?: {
    sellerNote?: string;
    adminNotes?: string;
    paymentLinkUrl?: string;
  },
): Promise<void> {
  const res = await apiFetch(`/api/reservations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, ...payload }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Action failed (${res.status})`);
  }
}

/** TNK-163 — per-card decline / unavailable. Doesn't change the
 *  reservation-level status; the seller can take per-item actions
 *  while the reservation stays open. */
export type ReservationItemAction = "release" | "remove_unavailable";

export async function patchReservationItem(
  reservationId: string,
  itemId: string,
  action: ReservationItemAction,
): Promise<void> {
  const res = await apiFetch(
    `/api/reservations/${encodeURIComponent(reservationId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action }),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Action failed (${res.status})`);
  }
}

/** TNK-127 phase 2 — seller sends a priced offer.
 *  Server validates `total === subtotal − discount + shipping` (0.01
 *  tolerance) so the client can do the math but the source of truth
 *  is server-side. `expiresAt` is an ISO datetime string. */
export interface SendOfferBody {
  subtotal: number;
  discount?: number;
  shipping?: number;
  total: number;
  message?: string;
  expiresAt: string;
}

export async function sendReservationOffer(
  reservationId: string,
  body: SendOfferBody,
): Promise<{ offer: ReservationOffer; reservationStatus: string }> {
  const res = await apiFetch(
    `/api/reservations/${encodeURIComponent(reservationId)}/offers`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `Send offer failed (${res.status})`);
  }
  return (await res.json()) as {
    offer: ReservationOffer;
    reservationStatus: string;
  };
}

export async function cancelReservationOffer(
  reservationId: string,
  offerId: string,
): Promise<{ offer: ReservationOffer; reservationStatus: string }> {
  const res = await apiFetch(
    `/api/reservations/${encodeURIComponent(reservationId)}/offers/${encodeURIComponent(offerId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "cancel" }),
    },
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `Cancel offer failed (${res.status})`);
  }
  return (await res.json()) as {
    offer: ReservationOffer;
    reservationStatus: string;
  };
}

/** TNK-127 phase 4 — seller accepts or declines a buyer's counter-offer.
 *  accept → AwaitingPayment; decline → Cancelled (releases the hold). */
export async function respondToBuyerCounter(
  reservationId: string,
  offerId: string,
  action: "accept" | "decline",
): Promise<{ offer: ReservationOffer; reservationStatus: string }> {
  const res = await apiFetch(
    `/api/reservations/${encodeURIComponent(reservationId)}/offers/${encodeURIComponent(offerId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action }),
    },
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `Action failed (${res.status})`);
  }
  return (await res.json()) as {
    offer: ReservationOffer;
    reservationStatus: string;
  };
}
